import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import {AsyncPipe} from '@angular/common';
import {AppState} from './store/store';
import {initializeApp} from './store/auth/auth.actions';
import {getActualLanguage} from './functions/translate-utils';
import {PreviewModeService} from './services/preview-mode/preview-mode.service';

@Component({
  selector: 'app-root',
  imports: [TranslatePipe, RouterOutlet, AsyncPipe],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {

  private store: Store<AppState> = inject(Store<AppState>);

  private translateService: TranslateService = inject(TranslateService);

  private previewModeService: PreviewModeService = inject(PreviewModeService);

  private destroyRef: DestroyRef = inject(DestroyRef);

  public previewMode$ = this.previewModeService.previewMode$;

  public sharedLinkData$ = this.previewModeService.sharedLinkData$;

  constructor() {
    this.initTranslations();
    this.store.dispatch(initializeApp());
    this.previewModeService.initialize();
  }

  ngOnInit(): void {
    this.setupUrlChangeHandling();
    this.setupSharedLinkDataHandling();
  }

  /**
   * Configures handling of URL changes to manage preview mode
   */
  private setupUrlChangeHandling(): void {
    this.previewModeService.handleUrlChanges().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => {
      this.previewModeService.processNavigationChange(event.url);
    });
  }

  /**
   * Sets up handling of shared link data to update the application state when data is available
   */
  private setupSharedLinkDataHandling(): void {
    this.sharedLinkData$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      if (data && this.previewModeService.isInPreviewMode()) {
        this.previewModeService.processSharedLinkData(data);
      }
    });
  }

  /**
   * Initializes translation service with the actual language
   */
  private initTranslations(): void {
    this.translateService.use(getActualLanguage());
  }

}
